import { WaveCloudAPI, WAVE_APP_ID } from "@vida/wave-core";
import { createWaveApiConfig, trackActivity } from "./waveSession";
import { downloadBlobText } from "./waveBlobDownload";
import { WAVE_AGENT_MODEL_ID } from "./waveAgentModel";

const TERMINAL = new Set(["COMPLETED", "FAILED", "CANCELLED", "TIMED_OUT"]);

async function resolveJobText(job: {
  providerUsageJson?: string | null;
  outputAssetKey?: string | null;
}): Promise<string> {
  const inline = extractOutputText(job.providerUsageJson);
  if (inline) return inline;
  const key = job.outputAssetKey?.trim();
  if (key) return downloadBlobText(key);
  throw new Error("Output inferenza todo non disponibile.");
}

function extractOutputText(providerUsageJson?: string | null): string | null {
  if (!providerUsageJson?.trim()) return null;
  try {
    const parsed = JSON.parse(providerUsageJson) as {
      outputText?: string;
      text?: string;
    };
    return parsed.outputText?.trim() || parsed.text?.trim() || null;
  } catch {
    return null;
  }
}

async function pollAIJob(jobId: string, timeoutMs = 60_000): Promise<string> {
  const cfg = createWaveApiConfig();
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const job = await WaveCloudAPI.getAIJob(cfg, jobId);
    if (TERMINAL.has(job.status)) {
      if (job.status !== "COMPLETED") {
        throw new Error(job.errorMessage ?? `Job ${job.status}`);
      }
      return resolveJobText(job);
    }
    await new Promise((r) => setTimeout(r, 1200));
  }
  throw new Error("Timeout inferenza todo.");
}

function parseInferenceJson(raw: string): {
  title?: string;
  dueDate?: string | null;
  priority?: string;
} | null {
  const trimmed = raw.trim();
  const start = trimmed.indexOf("{");
  const end = trimmed.lastIndexOf("}");
  if (start < 0 || end <= start) return null;
  try {
    return JSON.parse(trimmed.slice(start, end + 1)) as {
      title?: string;
      dueDate?: string | null;
      priority?: string;
    };
  } catch {
    return null;
  }
}

export type WaveTodoInferenceResult = {
  title: string;
  dueDateISO?: string;
  priority?: string;
};

/** Infer emoji + markdown title formatting for a Wave todo item (VibeDashboard parity). */
export async function inferWaveTodoTitle(
  waveId: string,
  rawText: string,
): Promise<WaveTodoInferenceResult | null> {
  const text = rawText.trim();
  if (!text) return null;

  const now = new Date();
  const nowString = now.toLocaleString("it-IT", {
    dateStyle: "medium",
    timeStyle: "short",
  });
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "Europe/Rome";

  const cfg = createWaveApiConfig();
  const params = {
    bedrock: {
      systemText:
        "Sei un assistente che arricchisce todo Wave. Rispondi SOLO con JSON valido.",
      userText:
        `Todo: "${text}"\nData/ora: ${nowString}\nTimezone: ${tz}\n\n` +
        `Rispondi: {"title":"...","dueDate":"YYYY-MM-DDTHH:mm"|"YYYY-MM-DD"|null,"priority":"none|low|medium|high"}\n` +
        `Regole title: 1) emoji pertinente all'inizio; 2) **grassetto** su 1-2 parole chiave; 3) nomi propri in ***grassetto corsivo***; NON riscrivere il testo.`,
      maxTokens: 256,
    },
    waveId,
  };

  const result = await WaveCloudAPI.submitAIJob(cfg, {
    kind: "BEDROCK_CONVERSE",
    provider: "BEDROCK",
    prompt: text,
    paramsJson: JSON.stringify(params),
    inputAssetKeys: [],
    appId: WAVE_APP_ID,
    modelId: WAVE_AGENT_MODEL_ID,
    feature: "wave.agent.todo_infer",
  });

  trackActivity("Todo Wave", `Inferenza titolo (job ${result.jobId.slice(0, 8)}…)`);
  const raw = await pollAIJob(result.jobId);
  const parsed = parseInferenceJson(raw);
  if (!parsed?.title?.trim()) return null;

  let dueDateISO: string | undefined;
  if (parsed.dueDate && parsed.dueDate !== "null") {
    dueDateISO = parsed.dueDate;
  }

  const validPriorities = ["none", "low", "medium", "high"];
  const priority = validPriorities.includes((parsed.priority ?? "").toLowerCase())
    ? parsed.priority!.toLowerCase()
    : undefined;

  return {
    title: parsed.title.trim(),
    dueDateISO,
    priority,
  };
}
